// `@sl/core/queries` — THE LIVE STATE: what a shell shows ABOUT an answer, derived once (T-208; ARCH §8.1, §10).
//
// Every factory in `queries.ts` hands its reader the whole `ApiResponse<T>` — `{ data, meta }` — because the shell
// has to say how old the figures are, whether something was left out, and whether we can even reach Meta. This
// module is the one place those facts are READ, so a screen renders a typed state and never a field of `meta`:
//
//   const live = liveState({ account, sync, meta: summary.data?.meta, connectivity: client.connectivity(),
//                            now: clock.now(), ctx: messages, deviceTimeZone });
//   <Chip tone={live.freshness.tone}>{render(live.freshness.message, messages)}</Chip>
//   {live.warnings.account.map(…)}      // the shell says these ONCE for the account (REQ-264 AC2)
//   {live.warnings.partial.map(…)}      // a screen says these about ITS own numbers
//
// Five rules shape the file, and each one is a test below.
//
// 1. **A demo account has no network state** (REQ-246 AC5, REQ-283 AC5). `meta.sampleData` / a `demo_*` mode is
//    checked FIRST and the answer is the sample label: never an age, never amber, never an offline banner, and
//    `connectivity` is not consulted at all. Its designed freshness states arrive the one way API §1.6 allows —
//    an armed QA fault, which puts the state in the ENVELOPE (`meta.stale`, `meta.warnings`), where this module
//    reads it like any other server fact. "Simulate offline" is a transport fault (it fails the read); it is not
//    a freshness state, which is why it cannot move anything here.
// 2. **The next-refresh instant comes from §2.3's `rateLimit.nextAttemptAt` or §1.6's `warning.rate_limited`
//    param — never from §12.2's `connection`**, which carries `{ state, reason }` and no instant at all (D-1065:
//    adding one there is a contract change). `nextAttemptAt` below takes the two sources and nothing else, so the
//    band is not a parameter of it and "next update at 14:50" cannot be built out of thin air: with no instant
//    anywhere the sentence is `null` and the caller falls back to the reason, which names no time.
// 3. **The sixty-minute line is API §1.6's, spelled once** (`isStaleAt`): older THAN 60 minutes is stale, so 59
//    and exactly 60 are fresh and 61 is not — the same boundary the C# `Freshness` block holds (T-205). The
//    client needs its own copy for one honest reason: `meta.stale` was decided at `meta.generatedAt`, and an
//    answer painted from the offline cache half an hour later has aged since. Server fact and client age may
//    therefore only ever ADD (`stale: true` is never argued with); they cannot disagree at the boundary, and a
//    test pins all three minutes against a server envelope.
// 4. **`dataAsOf: null` is not stale, it is a FIRST SYNC** (API §1.6, §2.3): there is no instant to be old, so the
//    label shows progress (`state.first_sync`) and never an empty age.
// 5. **Words stay in the catalogue.** Everything here answers a `Message` built from a key that already exists
//    (`state.*`, `warning.*`, `time.*`) or passes the server's own sentence through untouched. No new key was
//    needed for any state T-211 / T-214 draw, so nothing is owed on the C# side.

import type { ConnectivityState } from '../api/connectivity';
import { message } from '../messages/keys';
import { render, type MessageContext } from '../messages/render';
import { durationCount, timeAgo } from '../messages/time';
import type { ApiWarning, IsoDate, IsoInstant, Message, ResponseMeta } from '../types/primitives';
import type { AccountInfo, ConnectionState, SyncStatus } from '../types/session';
import { isDemoMode } from './staleTime';

// ─── 1. age, and API §1.6's sixty-minute line ───────────────────────────────────────────────────────────────

/** REQ-283 AC2: data older THAN this is amber "may be out of date". API §1.6's own number. */
export const STALE_AFTER_MS = 60 * 60_000;
/** REQ-283 AC2's second threshold: a page-level banner at three hours. Computed from `dataAsOf`, no new field. */
export const STALE_BANNER_AFTER_MS = 3 * 60 * 60_000;

/** How old the data in an answer is, in ms; `null` when nothing has been read yet. A future instant is `0`. */
export function dataAgeMs(dataAsOf: IsoInstant | null | undefined, now: number): number | null {
  if (!dataAsOf) return null;
  const at = new Date(dataAsOf).getTime();
  if (!Number.isFinite(at)) return null;
  return Math.max(0, now - at);
}

/** API §1.6's rule: `dataAsOf` OLDER than 60 minutes. 59 and exactly 60 are fresh; 61 is stale; `null` is not. */
export function isStaleAt(dataAsOf: IsoInstant | null | undefined, now: number): boolean {
  const age = dataAgeMs(dataAsOf, now);
  return age !== null && age > STALE_AFTER_MS;
}

// ─── 2. the four warnings, typed and attributed to a surface ────────────────────────────────────────────────

/** The four codes of this row: REQ-283 AC2, REQ-286 AC2, REQ-040 AC7 / §1.9, REQ-002 AC4. */
export const LIVE_WARNING_CODES = [
  'stale_data',
  'rate_limited',
  'partial_data',
  'connection_problem',
] as const;
export type LiveWarningCode = (typeof LIVE_WARNING_CODES)[number];

/**
 * The warnings that belong to the ACCOUNT and are said once by the shell — never again by a screen over its own
 * numbers (REQ-264 AC2: two bands saying the same thing is the defect that rule exists to prevent). Meta limiting
 * us and a connection we cannot make are conditions of the account, and the shell's band states both.
 *
 * This is the WEB's list (`SILENT_WARNINGS`, repeated on Home, Campaigns, Creatives and the entity page). The
 * phone's `BAND_OWNED_WARNINGS` names `rate_limited` alone, so a live `connection_problem` would raise the amber
 * "some numbers are missing" band there AND the shell's connection band — the one case where the two apps
 * disagree today. Nothing on screen moves until the apps adopt this (no server emits `connection_problem` on a
 * demo account); T-211 / T-214 take this list and delete their own.
 */
export const ACCOUNT_OWNED_WARNINGS: readonly LiveWarningCode[] = ['rate_limited', 'connection_problem']; // prettier-ignore
/** The code that means "these numbers are real but older than they look" rather than "these are incomplete". */
export const STALE_WARNINGS: readonly LiveWarningCode[] = ['stale_data'];

/** An answer's warnings, split by the surface that says them (`all` is the envelope's own order, untouched). */
export interface LiveWarnings {
  all: readonly ApiWarning[];
  /** `stale_data` — the age band. */
  stale: readonly ApiWarning[];
  /** Everything that means a figure or a row is missing: `partial_data` and the three §1.6 codes beside it. */
  partial: readonly ApiWarning[];
  /** `rate_limited` / `connection_problem` — the shell's band, once for the account. */
  account: readonly ApiWarning[];
}

/** The warning of one code, or `null`. The server's sentence is what a client renders; it is never rebuilt here. */
export function warningOf(
  meta: Pick<ResponseMeta, 'warnings'> | null | undefined,
  code: ApiWarning['code'],
): ApiWarning | null {
  return meta?.warnings?.find((w) => w.code === code) ?? null;
}

/** API §1.6's `warnings[]`, split by surface. A code this build does not know lands in `partial` — "something is
 *  missing and here is why" is what that band means, and a new code is more likely to be that than anything. */
export function liveWarnings(
  meta: Pick<ResponseMeta, 'warnings'> | null | undefined,
): LiveWarnings {
  const all = meta?.warnings ?? [];
  return {
    all,
    stale: all.filter((w) => STALE_WARNINGS.includes(w.code as LiveWarningCode)),
    partial: all.filter(
      (w) =>
        !STALE_WARNINGS.includes(w.code as LiveWarningCode) &&
        !ACCOUNT_OWNED_WARNINGS.includes(w.code as LiveWarningCode),
    ),
    account: all.filter((w) => ACCOUNT_OWNED_WARNINGS.includes(w.code as LiveWarningCode)),
  };
}

// ─── 3. §12.2's connection band ─────────────────────────────────────────────────────────────────────────────

/** The states that raise a band. `ok` is fine and `syncing_first_time` is progress, which the freshness label
 *  already says (REQ-002 AC2) — a band for it would say the same thing twice. */
export const CONNECTION_BAND_STATES: readonly ConnectionState[] = [
  'token_invalid',
  'permission_missing',
  'rate_limited',
  'unreachable',
  'account_problem',
];

export interface ConnectionNotice {
  state: ConnectionState;
  /** WHAT HAPPENED: the server's own `reason`, else the catalogue key for the state. Never retyped per app. */
  reason: Message;
  /** Does this state raise the shell's band? `syncing_first_time` does not (the freshness label says it). */
  band: boolean;
}

/**
 * §12.2's `connection` as a typed notice, or `null` when the connection is `ok`. The two halves are kept apart as
 * the apps' bands already keep them: the REASON is the wire's (or the catalogue's key for the state), and what it
 * means for your ads and what to do stays each app's own sentence (D-540) — no key carries it.
 */
export function connectionNotice(
  connection: AccountInfo['connection'] | null | undefined,
): ConnectionNotice | null {
  if (!connection || connection.state === 'ok') return null;
  const state = connection.state;
  return {
    state,
    reason: connection.reason ?? message(`state.connection.${state}`),
    band: CONNECTION_BAND_STATES.includes(state),
  };
}

// ─── 4. "next update at 14:50" ──────────────────────────────────────────────────────────────────────────────

/** The two places an instant for the next attempt may come from. §12.2's `connection` is deliberately NOT one of
 *  them: it carries `{ state, reason }` and no instant (API §12.2, D-1065), so it cannot be passed here. */
export interface NextAttemptSources {
  /** §2.3 `SyncStatus.rateLimit.nextAttemptAt` — the backend's own stored instant; read first. */
  sync?: Pick<SyncStatus, 'rateLimit'> | null;
  /** §1.6 `warning.rate_limited`'s `nextAttemptAt` param — the same instant, on an answer that carries no sync. */
  meta?: Pick<ResponseMeta, 'warnings'> | null;
}

/** The instant Meta will let us read again, or `null` when nothing on the wire states one. */
export function nextAttemptAt(sources: NextAttemptSources): IsoInstant | null {
  const stored = sources.sync?.rateLimit?.nextAttemptAt;
  if (stored) return stored;
  const param = warningOf(sources.meta, 'rate_limited')?.message.params.nextAttemptAt;
  return param?.t === 'instant' ? param.v : null;
}

/**
 * REQ-286 AC2's sentence — "Limited by Meta — next update at 2:50 pm" (`state.rate_limited`) — or `null` when no
 * instant is known, because the key has nowhere to put a time it was not given. A caller with no sentence falls
 * back to the connection's own reason (`state.connection.rate_limited`), which names no time and needs none.
 */
export function rateLimitedMessage(sources: NextAttemptSources): Message | null {
  const at = nextAttemptAt(sources);
  return at === null ? null : message('state.rate_limited', { nextAttemptAt: { t: 'instant', v: at } }); // prettier-ignore
}

/**
 * BUG-226: the rate-limited band's sentence when it also carries the AGE of the numbers (REQ-283 AC2's
 * three-hour threshold reached while REQ-286 AC2 has a band up) — with the next attempt where one is known,
 * without it otherwise. One band, one fact (REQ-264 AC2).
 */
export function rateLimitedSinceMessage(asOf: IsoInstant, at: IsoInstant | null): Message {
  return at === null
    ? message('state.rate_limited_since', { asOf: { t: 'instant', v: asOf } })
    : message('state.rate_limited_stale', {
        asOf: { t: 'instant', v: asOf },
        nextAttemptAt: { t: 'instant', v: at },
      });
}

// ─── 5. the freshness label ─────────────────────────────────────────────────────────────────────────────────

/**
 * The sentence the freshness label shows, for an account and its sync status — REQ-283.
 *
 * **Lifted verbatim from `apps/web/src/app/shell/Freshness.tsx` and its line-for-line copy on the phone**
 * (`DECLARED_TWINS` names the pair and its exit: "`@sl/core` beside `timeAgo`"). Same signature, same answers, so
 * adoption is one import; T-211 / T-214 delete the two copies and the declaration with them.
 *
 * REQ-283 AC5 is the rule that shapes it: a demo has no fetch to be fresh or stale about, whatever the sync says.
 */
export function freshnessMessage(
  account: Pick<AccountInfo, 'mode' | 'dataAsOf'>,
  sync: Pick<SyncStatus, 'state' | 'dataAsOf' | 'historyLoadedBackTo'> | undefined | null,
  now: number,
  ctx: MessageContext,
  deviceTimeZone: string,
): Message {
  return freshnessLabelOf({
    account,
    sync,
    // The two apps' own chain, kept exactly: the sync status, else the account record.
    asOf: sync?.dataAsOf ?? account.dataAsOf ?? null,
    now,
    ctx,
    deviceTimeZone,
  });
}

/** The four branches of the label, over an ALREADY-RESOLVED `asOf`, so `liveFreshness` (which also consults the
 *  answer's own envelope) and `freshnessMessage` (the apps' signature) cannot word the same state differently. */
function freshnessLabelOf(input: {
  account: Pick<AccountInfo, 'mode' | 'dataAsOf'>;
  sync: Pick<SyncStatus, 'state' | 'historyLoadedBackTo'> | undefined | null;
  asOf: IsoInstant | null;
  now: number;
  ctx: MessageContext;
  deviceTimeZone: string;
}): Message {
  const { account, sync, asOf } = input;
  // REQ-283 AC5: a demo has no fetch to be fresh or stale about, whatever the sync status says.
  if (isDemoMode(account.mode)) return message('state.sample_data');
  if (sync?.state === 'running') {
    return sync.historyLoadedBackTo === null && account.dataAsOf === null
      ? message('state.first_sync')
      : message('state.refreshing');
  }
  if (!asOf) return message('state.first_sync');
  return message('state.updated', {
    ago: { t: 'text', v: render(timeAgo(asOf, input.now, input.deviceTimeZone), input.ctx) },
  });
}

export type FreshnessTone = 'sample' | 'fresh' | 'stale';

export interface LiveFreshness {
  /** The chip's sentence: `state.sample_data` · `state.first_sync` · `state.refreshing` · `state.updated`. */
  message: Message;
  /** `sample` is never amber (REQ-283 AC5); `stale` is REQ-283 AC2's amber "may be out of date". */
  tone: FreshnessTone;
  /** The amber chip's own words, beside the label — `null` unless the tone is `stale`. */
  staleLabel: Message | null;
  /** REQ-283 AC2's page-level banner at three hours: the server's `stale_data` sentence, else `warning.stale_data`
   *  composed from this instant. `null` below three hours. */
  banner: Message | null;
  /** The instant the figures are from — the sync's, else the account's. `null` before the first sync. */
  asOf: IsoInstant | null;
  ageMs: number | null;
}

export interface FreshnessInput {
  account: Pick<AccountInfo, 'mode' | 'dataAsOf'>;
  sync?: Pick<SyncStatus, 'state' | 'dataAsOf' | 'historyLoadedBackTo'> | null;
  /** The envelope of the read this label describes, where there is one: its `stale` and `warnings` are server
   *  facts and win on their own terms (they can only ADD to the client's age rule — see rule 3 in the header),
   *  and its `dataAsOf` is the freshness of the DATA IN THAT ANSWER (§12.1) — which is what a page-level "these
   *  numbers are from …" is about, and what an armed `stale` fault moves on the figure routes (D-1020). */
  meta?: Pick<ResponseMeta, 'stale' | 'warnings' | 'sampleData' | 'dataAsOf'> | null;
  now: number;
  ctx: MessageContext;
  deviceTimeZone: string;
}

/** The freshness label, its tone and the three-hour banner — REQ-283 AC1, AC2, AC5. */
export function liveFreshness(input: FreshnessInput): LiveFreshness {
  const { account, sync, meta, now } = input;
  const sample = isDemoMode(account.mode) || meta?.sampleData === true;
  // The sync status is the authority on the last successful pass where a reader has it (T-205: `dataAsOf` IS
  // `sl_sync_state.last_success_at`, one source); otherwise the answer's own envelope, then the account record.
  // **`meta.generatedAt` is never in this chain**: §12.1 calls it the clock on every answer, so reading it would
  // make a three-day-old account say "Updated just now" every time anything was rendered.
  const asOf = sync?.dataAsOf ?? meta?.dataAsOf ?? account.dataAsOf ?? null;
  const ageMs = dataAgeMs(asOf, now);
  const label = freshnessLabelOf({
    account,
    sync,
    asOf,
    now,
    ctx: input.ctx,
    deviceTimeZone: input.deviceTimeZone,
  });
  if (sample)
    return { message: label, tone: 'sample', staleLabel: null, banner: null, asOf, ageMs };
  // The server's own fact, the server's own warning, and the age as it stands now — any one of them is amber, and
  // they cannot disagree at the boundary because the client's rule IS §1.6's (rule 3).
  const stale = meta?.stale === true || warningOf(meta, 'stale_data') !== null || isStaleAt(asOf, now); // prettier-ignore
  const old = ageMs !== null && ageMs >= STALE_BANNER_AFTER_MS;
  const banner = !old
    ? null
    : (warningOf(meta, 'stale_data')?.message ??
      (asOf === null ? null : message('warning.stale_data', { asOf: { t: 'instant', v: asOf } })));
  return {
    message: label,
    tone: stale ? 'stale' : 'fresh',
    staleLabel: stale ? message('state.may_be_out_of_date') : null,
    banner,
    asOf,
    ageMs,
  };
}

// ─── 6. the refresh control ─────────────────────────────────────────────────────────────────────────────────

export interface RefreshState {
  /** Can the owner ask for newer numbers right now? A demo always can (REQ-283 AC5, API §2.4). */
  available: boolean;
  /** §2.3 `nextManualRefreshAt` — `null` means "available now", never "unknown". */
  nextAt: IsoInstant | null;
  /** REQ-283 AC1's "says when the next refresh is possible": `state.refresh_wait`, or `null` when none is left. */
  wait: Message | null;
  /** A pass is running (§2.3 `state`), so the control is busy rather than refused. */
  running: boolean;
}

/** The refresh control's state — REQ-283 AC1 / AC5, REQ-286 AC1, API §2.3 / §2.4. */
export function refreshState(input: {
  account: Pick<AccountInfo, 'mode'>;
  sync?: Pick<SyncStatus, 'state' | 'nextManualRefreshAt'> | null;
  meta?: Pick<ResponseMeta, 'sampleData'> | null;
  now: number;
}): RefreshState {
  const running = input.sync?.state === 'running';
  // REQ-283 AC5 / API §2.4: a demo refresh is never refused and never waits — `nextManualRefreshAt` is always null
  // there, and this says so even when a fixture forgets.
  if (isDemoMode(input.account.mode) || input.meta?.sampleData === true)
    return { available: true, nextAt: null, wait: null, running };
  const nextAt = input.sync?.nextManualRefreshAt ?? null;
  const left = nextAt === null ? 0 : new Date(nextAt).getTime() - input.now;
  if (nextAt === null || !Number.isFinite(left) || left <= 0)
    return { available: true, nextAt: null, wait: null, running };
  return {
    available: false,
    nextAt,
    wait: message('state.refresh_wait', { wait: durationCount(left) }),
    running,
  };
}

// ─── 7. the first sync (REQ-002 AC2) ────────────────────────────────────────────────────────────────────────

export interface FirstSyncProgress {
  running: boolean;
  steps: SyncStatus['steps'];
  /** The step to name: the one running, else the first that has not finished, else `null` (all done). */
  step: SyncStatus['steps'][number] | null;
  done: number;
  total: number;
  /** How far back history has loaded — `null` until a pass has completed (API §2.3). Ranges before it are "still
   *  loading", never zeros (REQ-002 AC2). */
  historyLoadedBackTo: IsoDate | null;
  /** The step's OWN percentage, passed through from the wire. Nothing here computes a figure (API §1.4). */
  progressPct: number | null;
  /** The label while there is no age to show: `state.first_sync`. */
  message: Message;
}

/**
 * REQ-002 AC2's first sync, or `null` once this account has data. The test is `dataAsOf === null` on both the
 * account and the sync — API §2.3's own rule — so a later refresh of a loaded account is not mistaken for a first
 * one, and a label never shows an empty age.
 */
export function firstSyncProgress(
  sync: Pick<SyncStatus, 'state' | 'steps' | 'dataAsOf' | 'historyLoadedBackTo'> | null | undefined,
  account?: Pick<AccountInfo, 'dataAsOf'> | null,
): FirstSyncProgress | null {
  const asOf = sync?.dataAsOf ?? account?.dataAsOf ?? null;
  if (asOf !== null) return null;
  const steps = sync?.steps ?? [];
  const step =
    steps.find((s) => s.state === 'running') ?? steps.find((s) => s.state !== 'done') ?? null;
  return {
    running: sync?.state === 'running',
    steps,
    step,
    done: steps.filter((s) => s.state === 'done').length,
    total: steps.length,
    historyLoadedBackTo: sync?.historyLoadedBackTo ?? null,
    progressPct: step?.progressPct ?? null,
    message: message('state.first_sync'),
  };
}

// ─── 8. offline (REQ-246) ───────────────────────────────────────────────────────────────────────────────────

export interface OfflineState {
  offline: boolean;
  /** "Offline · as of 14:32" (REQ-246 AC1) — the instant the figures on screen are from. */
  asOf: IsoInstant | null;
  message: Message | null;
}

/**
 * REQ-246 AC1's banner, and REQ-246 AC5's exception in one expression: **a demo account is never offline.** Its
 * server runs on the device, so there is nothing to be cut off from — and the signal is read from the CLIENT
 * (`ApiClient.connectivity()`), never from a browser or NetInfo directly, which is what makes that structural:
 * `DemoApiClient` has no connectivity source to consult and answers `online` unless QA armed the switch.
 */
export function offlineState(input: {
  connectivity?: ConnectivityState | null;
  account: Pick<AccountInfo, 'mode'>;
  meta?: Pick<ResponseMeta, 'sampleData'> | null;
  asOf: IsoInstant | null;
}): OfflineState {
  const sample = isDemoMode(input.account.mode) || input.meta?.sampleData === true;
  if (sample || input.connectivity !== 'offline')
    return { offline: false, asOf: input.asOf, message: null };
  return {
    offline: true,
    asOf: input.asOf,
    message:
      input.asOf === null
        ? message('state.offline')
        : message('state.offline_since', { asOf: { t: 'instant', v: input.asOf } }),
  };
}

// ─── 9. the whole state, in one call ────────────────────────────────────────────────────────────────────────

export interface LiveStateInput extends FreshnessInput {
  account: AccountInfo;
  sync?: SyncStatus | null;
  meta?: ResponseMeta | null;
  /** `ApiClient.connectivity()`. Absent is treated as online — a state that has not been read promises nothing. */
  connectivity?: ConnectivityState | null;
}

export interface LiveState {
  /** `meta.sampleData` or a `demo_*` mode: no age, never amber, no connectivity (REQ-283 AC5, REQ-246 AC5). */
  sample: boolean;
  freshness: LiveFreshness;
  /** §12.2's band, said once for the account (REQ-264 AC2). */
  connection: ConnectionNotice | null;
  warnings: LiveWarnings;
  /** REQ-286 AC2's sentence, from §2.3 or §1.6 and never from the band; `null` when no instant is known. */
  rateLimited: Message | null;
  /**
   * BUG-226 / REQ-264 AC2: the shell already says how old the numbers are, in a band - the three-hour banner's
   * (`freshness.banner`) or the rate-limited band's that carries it - so a page must not repeat a `stale_data`
   * warning of its own.
   */
  shellSaysAge: boolean;
  refresh: RefreshState;
  /** REQ-002 AC2, or `null` once the account has data. */
  firstSync: FirstSyncProgress | null;
  offline: OfflineState;
  /** REQ-283 AC3's "so far — still coming in", off `meta.todayIsPartial`. */
  todayIsPartial: boolean;
  todaySoFar: Message | null;
  /** Ranges before this date are still loading, never zeros (REQ-002 AC2). */
  historyLoadedBackTo: IsoDate | null;
}

/** Everything a shell needs to say about one answer — the nine parts above, resolved once per render. */
export function liveState(input: LiveStateInput): LiveState {
  const { account, sync, meta } = input;
  const sample = isDemoMode(account.mode) || meta?.sampleData === true;
  const freshness = liveFreshness(input);
  const connection = connectionNotice(account.connection);
  const todayIsPartial = meta?.todayIsPartial === true;
  // BUG-226 (REQ-264 AC2: a fact is said once). While the account is rate-limited AND the numbers are old enough
  // for REQ-283 AC2's three-hour banner, the shell would draw two bands about one reading - "Limited by Meta -
  // next update at …" and "These numbers are from … - we haven't been able to refresh since" - and a page's
  // partial band a third. The rate-limited band carries the age instead, in one sentence, and the three-hour
  // banner steps aside; `shellSaysAge` tells a page that the shell already says it, whichever band does.
  const foldedAge =
    connection?.state === 'rate_limited' && freshness.banner !== null ? freshness.asOf : null;
  return {
    sample,
    freshness: foldedAge === null ? freshness : { ...freshness, banner: null },
    connection,
    warnings: liveWarnings(meta),
    rateLimited:
      foldedAge === null
        ? rateLimitedMessage({ sync, meta })
        : rateLimitedSinceMessage(foldedAge, nextAttemptAt({ sync, meta })),
    shellSaysAge: freshness.banner !== null,
    refresh: refreshState({ account, sync, meta, now: input.now }),
    firstSync: firstSyncProgress(sync, account),
    offline: offlineState({
      connectivity: input.connectivity,
      account,
      meta,
      asOf: freshness.asOf,
    }),
    todayIsPartial,
    todaySoFar: todayIsPartial ? message('state.today.so_far') : null,
    historyLoadedBackTo: meta?.historyLoadedBackTo ?? sync?.historyLoadedBackTo ?? null,
  };
}
